import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ArrowUpDown, Download, X, Settings2 } from 'lucide-react';
import { clientsApi, type ClientsParams } from '../../api/endpoints/clients';
import { useDebounce } from '../../hooks/useDebounce';
import { StatCard } from '../../components/ui/Card';
import { StatusBadge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { MetricBar } from '../../components/ui/MetricBar';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatTimeAgo, formatUptime } from '../../utils/formatters';
import type { Client } from '../../types/client';

function computeHealth(c: Client): string {
  if (!c.metrics) return 'no-data';
  const age = c.metrics.collected_at ? (Date.now() - new Date(c.metrics.collected_at).getTime()) / 1000 : 999;
  if (age > 120) return 'stale';
  if (c.metrics.tunnel_status === 'healthy' && c.metrics.ssh_connection === 'up') return 'healthy';
  if (c.metrics.tunnel_status === 'degraded') return 'degraded';
  if (c.metrics.ssh_connection === 'down' || c.metrics.tunnel_status === 'down') return 'down';
  return 'degraded';
}

const healthColors: Record<string, string> = {
  healthy: 'bg-green-500',
  degraded: 'bg-yellow-500',
  down: 'bg-red-500',
  stale: 'bg-gray-400',
  'no-data': 'bg-gray-300',
};

// Column definitions for the clients table
type ColumnDef = { key: string; label: string; defaultVisible: boolean };
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'health', label: 'Health', defaultVisible: true },
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'deployment', label: 'Deployment', defaultVisible: true },
  { key: 'iot_ip', label: 'IOT IP', defaultVisible: true },
  { key: 'public_ip', label: 'Public IP', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'cpu', label: 'CPU', defaultVisible: true },
  { key: 'memory', label: 'Memory', defaultVisible: true },
  { key: 'disk', label: 'Disk', defaultVisible: true },
  { key: 'uptime', label: 'Uptime', defaultVisible: true },
  { key: 'mongodb', label: 'MongoDB', defaultVisible: true },
  { key: 'pm2', label: 'PM2', defaultVisible: true },
  { key: 'last_seen', label: 'Last Seen', defaultVisible: true },
  { key: 'tenant', label: 'Tenant', defaultVisible: false },
  { key: 'hostname', label: 'Hostname', defaultVisible: false },
  { key: 'latency', label: 'Latency', defaultVisible: false },
  { key: 'partner', label: 'Partner', defaultVisible: false },
];

const COLUMNS_STORAGE_KEY = 'clients-visible-columns';
function loadVisibleColumns(): Set<string> {
  try {
    const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (saved) return new Set(JSON.parse(saved));
  } catch { /* ignore */ }
  return new Set(TABLE_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
}

const EXPORT_FIELDS: { key: string; label: string; group: string }[] = [
  // Identity
  { key: 'client_id', label: 'Client ID', group: 'Identity' },
  { key: 'client_name', label: 'Client Name', group: 'Identity' },
  { key: 'hostname', label: 'Hostname', group: 'Identity' },
  { key: 'status', label: 'Status', group: 'Identity' },
  // Network
  { key: 'local_ip', label: 'IOT IP', group: 'Network' },
  { key: 'public_ip', label: 'Public IP', group: 'Network' },
  { key: 'port', label: 'SSH Port', group: 'Network' },
  { key: 'api_port', label: 'API Port', group: 'Network' },
  // Deployment
  { key: 'deployment_name', label: 'Deployment Name', group: 'Deployment' },
  { key: 'deployment_id', label: 'Deployment ID', group: 'Deployment' },
  { key: 'tenant_name', label: 'Tenant Name', group: 'Deployment' },
  { key: 'tenant_id', label: 'Tenant ID', group: 'Deployment' },
  { key: 'subdomain', label: 'Subdomain', group: 'Deployment' },
  { key: 'partner_name', label: 'Partner', group: 'Deployment' },
  // Metrics
  { key: 'cpu_percent', label: 'CPU %', group: 'Metrics' },
  { key: 'memory_percent', label: 'Memory %', group: 'Metrics' },
  { key: 'disk_percent', label: 'Disk %', group: 'Metrics' },
  { key: 'latency_ms', label: 'Latency (ms)', group: 'Metrics' },
  { key: 'uptime_seconds', label: 'Uptime (s)', group: 'Metrics' },
  { key: 'tunnel_status', label: 'Tunnel Status', group: 'Metrics' },
  { key: 'ssh_connection', label: 'SSH Connection', group: 'Metrics' },
  { key: 'reconnect_count', label: 'Reconnect Count', group: 'Metrics' },
  { key: 'last_error', label: 'Last Error', group: 'Metrics' },
  // Other
  { key: 'description', label: 'Description', group: 'Other' },
  { key: 'notes', label: 'Notes', group: 'Other' },
  { key: 'tags', label: 'Tags', group: 'Other' },
  { key: 'last_seen', label: 'Last Seen', group: 'Other' },
  { key: 'createdAt', label: 'Created At', group: 'Other' },
];

const DEFAULT_EXPORT_FIELDS = [
  'client_id', 'client_name', 'status', 'deployment_name', 'tenant_name',
  'local_ip', 'public_ip', 'cpu_percent', 'memory_percent', 'disk_percent', 'last_seen',
];

function ExportModal({ onClose, filters }: { onClose: () => void; filters: Record<string, string> }) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(DEFAULT_EXPORT_FIELDS));
  const [exporting, setExporting] = useState(false);

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(EXPORT_FIELDS.map(f => f.key)));
  const selectNone = () => setSelected(new Set());

  const groups = EXPORT_FIELDS.reduce<Record<string, typeof EXPORT_FIELDS>>((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});

  const handleExport = async () => {
    if (selected.size === 0) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set('fields', [...selected].join(','));
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      if (filters.deployment) params.set('deployment', filters.deployment);
      if (filters.sort) params.set('sort', filters.sort);
      if (filters.order) params.set('order', filters.order);

      const res = await fetch(`/api/clients/export?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch {
      alert('Failed to export clients');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Export Clients to CSV</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">{selected.size} of {EXPORT_FIELDS.length} fields selected</span>
          <div className="flex gap-2">
            <button onClick={selectAll} className="text-xs text-purple-600 hover:text-purple-800 dark:text-purple-400">Select All</button>
            <button onClick={selectNone} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400">Clear</button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-4 py-3">
          {Object.entries(groups).map(([group, fields]) => (
            <div key={group} className="mb-3">
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">{group}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {fields.map(f => (
                  <label key={f.key} className="flex items-center gap-2 cursor-pointer py-0.5">
                    <input
                      type="checkbox"
                      checked={selected.has(f.key)}
                      onChange={() => toggle(f.key)}
                      className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{f.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <p className="text-[10px] text-gray-400 dark:text-gray-500">Exports all matching clients (current filters applied)</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md">
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || selected.size === 0}
              className="px-4 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Download size={12} />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ClientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitial = useRef(true);

  // Read initial state from URL params
  const [page, setPageState] = useState(() => parseInt(searchParams.get('page') || '1') || 1);
  const [limit, setLimitState] = useState(() => parseInt(searchParams.get('limit') || '50') || 50);
  const [search, setSearchState] = useState(() => searchParams.get('q') || '');
  const [status, setStatusState] = useState(() => searchParams.get('status') || '');
  const [deployment, setDeploymentState] = useState(() => searchParams.get('deployment') || '');
  const [sort, setSortState] = useState(() => searchParams.get('sort') || 'client_name');
  const [order, setOrderState] = useState<'asc' | 'desc'>(() => (searchParams.get('order') as 'asc' | 'desc') || 'asc');

  const debouncedSearch = useDebounce(search);

  // Sync state to URL only when debounced values settle — never on every keystroke
  useEffect(() => {
    if (isInitial.current) { isInitial.current = false; return; }
    const next = new URLSearchParams();
    if (debouncedSearch) next.set('q', debouncedSearch);
    if (status) next.set('status', status);
    if (deployment) next.set('deployment', deployment);
    if (sort !== 'client_name') next.set('sort', sort);
    if (order !== 'asc') next.set('order', order);
    if (limit !== 50) next.set('limit', String(limit));
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, status, deployment, sort, order, limit, page, setSearchParams]);

  const setPage = (v: number) => setPageState(v);
  const setLimit = (v: number) => { setLimitState(v); setPageState(1); };
  const setStatus = (v: string) => { setStatusState(v); setPageState(1); };
  const setDeployment = (v: string) => { setDeploymentState(v); setPageState(1); };

  const params: ClientsParams = {
    page, limit, status, search: debouncedSearch, deployment, sort, order,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['clients', params],
    queryFn: () => clientsApi.list(params),
    refetchInterval: 15000,
    placeholderData: (prev) => prev,
  });

  const [showExport, setShowExport] = useState(false);
  const [visibleCols, setVisibleCols] = useState<Set<string>>(loadVisibleColumns);
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef<HTMLDivElement>(null);

  const toggleColumn = (key: string) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const isCol = (key: string) => visibleCols.has(key);

  // Close column picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target as Node)) {
        setShowColPicker(false);
      }
    };
    if (showColPicker) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColPicker]);

  const toggleSort = (field: string) => {
    setSortState(field);
    setOrderState(sort === field ? (order === 'asc' ? 'desc' : 'asc') : 'asc');
    setPageState(1);
  };

  const clients = data?.clients || [];
  const pagination = data?.pagination;
  const stats = data?.stats || { total: 0, connected: 0, disconnected: 0 };
  const summary = data?.summary || { healthy: 0, degraded: 0, down: 0 };

  if (isLoading && !data) return <PageSpinner />;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-gray-800 dark:text-white">Clients</h1>
        <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">Manage all tunnel client connections</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <div className="cursor-pointer" onClick={() => setStatus('')}>
          <StatCard label="Total" value={stats.total} color="gray" selected={status === ''} />
        </div>
        <div className="cursor-pointer" onClick={() => setStatus('connected')}>
          <StatCard label="Connected" value={stats.connected} color="green" selected={status === 'connected'} />
        </div>
        <div className="cursor-pointer" onClick={() => setStatus('disconnected')}>
          <StatCard label="Disconnected" value={stats.disconnected} color="red" selected={status === 'disconnected'} />
        </div>
        <StatCard label="Pending" value={stats.total - stats.connected - stats.disconnected} color="yellow" />
        <StatCard label="Healthy" value={summary.healthy} color="green" />
        <StatCard label="Degraded" value={summary.degraded} color="yellow" />
        <StatCard label="Down" value={summary.down} color="red" />
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-[#111] rounded-md shadow-sm border border-gray-200 dark:border-gray-800 p-3 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[256px] relative">
            <Search size={16} className="absolute left-2.5 top-2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearchState(e.target.value); setPageState(1); }}
              placeholder="Search by name, tenant, deployment, ID, hostname, IP..."
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
            />
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white">
            <option value="">All Status</option>
            <option value="connected">Connected</option>
            <option value="disconnected">Disconnected</option>
            <option value="registered">Registered</option>
            <option value="pending">Pending</option>
          </select>
          <select value={deployment} onChange={e => setDeployment(e.target.value)} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white">
            <option value="">All Deployments</option>
            <option value="unmapped">Not Mapped</option>
            <option value="mapped">Mapped</option>
          </select>
          <select value={limit} onChange={e => setLimit(Number(e.target.value))} className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white">
            <option value={25}>25/page</option>
            <option value={50}>50/page</option>
            <option value={100}>100/page</option>
            <option value={200}>200/page</option>
          </select>
          <div className="relative" ref={colPickerRef}>
            <button
              onClick={() => setShowColPicker(v => !v)}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-300"
              title="Toggle columns"
            >
              <Settings2 size={14} />
              Columns
            </button>
            {showColPicker && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-52">
                <p className="text-[10px] font-semibold text-gray-400 uppercase mb-2">Toggle Columns</p>
                {TABLE_COLUMNS.map(col => (
                  <label key={col.key} className="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visibleCols.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                      className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{col.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setShowExport(true)}
            className="px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center gap-1 text-xs font-medium"
          >
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          filters={{ status, search: debouncedSearch, deployment, sort, order }}
        />
      )}

      {/* Table */}
      <div className="bg-white dark:bg-[#111] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
        <Table>
          <Thead>
            <tr>
              {isCol('health') && <Th>Health</Th>}
              {isCol('name') && <Th sortable onClick={() => toggleSort('client_name')}>
                <span className="flex items-center gap-1">Name <ArrowUpDown size={12} /></span>
              </Th>}
              {isCol('deployment') && <Th>Deployment</Th>}
              {isCol('tenant') && <Th>Tenant</Th>}
              {isCol('hostname') && <Th>Hostname</Th>}
              {isCol('iot_ip') && <Th>IOT IP</Th>}
              {isCol('public_ip') && <Th>Public IP</Th>}
              {isCol('status') && <Th>Status</Th>}
              {isCol('cpu') && <Th>CPU</Th>}
              {isCol('memory') && <Th>Memory</Th>}
              {isCol('disk') && <Th>Disk</Th>}
              {isCol('uptime') && <Th>Uptime</Th>}
              {isCol('latency') && <Th>Latency</Th>}
              {isCol('mongodb') && <Th>MongoDB</Th>}
              {isCol('pm2') && <Th>PM2</Th>}
              {isCol('partner') && <Th>Partner</Th>}
              {isCol('last_seen') && <Th sortable onClick={() => toggleSort('last_seen')}>
                <span className="flex items-center gap-1">Last Seen <ArrowUpDown size={12} /></span>
              </Th>}
            </tr>
          </Thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {clients.map(client => {
              const health = computeHealth(client);
              const mongoStatus = client.metrics?.mongodb_status;
              const pm2Status = client.metrics?.pm2_status;
              return (
                <Tr key={client._id} onClick={() => navigate(`/clients/${client.client_id}`)}>
                  {isCol('health') && <Td>
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${healthColors[health]} ${health === 'healthy' ? 'animate-pulse-dot' : ''}`} title={health} />
                  </Td>}
                  {isCol('name') && <Td>
                    <div>
                      <span className="font-medium text-purple-600 dark:text-purple-400 hover:underline">{client.client_name || client.client_id}</span>
                      <p className="text-[10px] text-gray-400">{client.client_id}</p>
                    </div>
                  </Td>}
                  {isCol('deployment') && <Td>
                    <span className="text-gray-600 dark:text-gray-400">{client.deployment_name || '-'}</span>
                  </Td>}
                  {isCol('tenant') && <Td>
                    <span className="text-gray-600 dark:text-gray-400">{client.tenant_name || '-'}</span>
                  </Td>}
                  {isCol('hostname') && <Td>
                    <span className="font-mono text-[10px] text-gray-600 dark:text-gray-400">{client.hostname || '-'}</span>
                  </Td>}
                  {isCol('iot_ip') && <Td><span className="font-mono text-[10px] text-gray-600 dark:text-gray-400">{client.local_ip || '-'}</span></Td>}
                  {isCol('public_ip') && <Td><span className="font-mono text-[10px] text-gray-600 dark:text-gray-400">{client.public_ip || '-'}</span></Td>}
                  {isCol('status') && <Td><StatusBadge status={client.status} /></Td>}
                  {isCol('cpu') && <Td><MetricBar value={client.metrics?.cpu_percent} color="blue" /></Td>}
                  {isCol('memory') && <Td><MetricBar value={client.metrics?.memory_percent} color="indigo" /></Td>}
                  {isCol('disk') && <Td><MetricBar value={client.metrics?.disk_percent} color="purple" /></Td>}
                  {isCol('uptime') && <Td>{client.metrics?.uptime_seconds ? formatUptime(client.metrics.uptime_seconds) : '-'}</Td>}
                  {isCol('latency') && <Td>
                    <span className={`text-xs ${(client.metrics?.latency_ms || 0) > 200 ? 'text-red-600' : (client.metrics?.latency_ms || 0) > 100 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {client.metrics?.latency_ms != null ? `${client.metrics.latency_ms}ms` : '-'}
                    </span>
                  </Td>}
                  {isCol('mongodb') && <Td>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      mongoStatus === 'up' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      mongoStatus === 'down' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                      'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'
                    }`}>{mongoStatus || '-'}</span>
                  </Td>}
                  {isCol('pm2') && <Td>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      pm2Status?.includes('online') && !pm2Status?.includes('errored') ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                      pm2Status?.includes('errored') ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                      pm2Status?.includes('stopped') ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                      'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'
                    }`}>{pm2Status || '-'}</span>
                  </Td>}
                  {isCol('partner') && <Td>
                    <span className="text-gray-600 dark:text-gray-400 text-xs">{client.partner_name || '-'}</span>
                  </Td>}
                  {isCol('last_seen') && <Td>{formatTimeAgo(client.last_seen)}</Td>}
                </Tr>
              );
            })}
            {clients.length === 0 && (
              <tr><td colSpan={visibleCols.size} className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No clients found</td></tr>
            )}
          </tbody>
        </Table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {((pagination.page - 1) * limit) + 1}–{Math.min(pagination.page * limit, pagination.total)} of {pagination.total} clients
          </p>
          <Pagination
            page={pagination.page}
            totalPages={pagination.pages}
            onPageChange={setPage}
            className="mt-0"
          />
        </div>
      )}
    </div>
  );
}
